import { useObjectFeatureFlagDetails } from '@/hooks/useFeatureFlags';
import { useGetWealthsQuery } from '@/store/wealthsApi';
import { WARootState } from '@/store/store';
import { isSupportedAggregator } from '@/utils/aggregatorCredentials';
import { isActionable, resolveFlow } from '@/utils/connectionError';
import { deriveAdvisorGroupId } from '@/utils/deriveAdvisorGroupId';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { isEmbeddableWidgetBundle } from '@/integrations/embeddableWidgetPortalUtils';
import { useSelector } from 'react-redux';

/** Datadog object-flag payload listing advisor groups allowed to see connection errors. */
export type ConnectionErrorFlagValue = {
  advisorGroupIds: number[];
} & Record<string, unknown>;

export const DEFAULT_CONNECTION_ERROR_FLAG: ConnectionErrorFlagValue = {
  advisorGroupIds: [],
};

export function isConnectionErrorEnabledForAdvisorGroup(
  flagValue: ConnectionErrorFlagValue,
  advisorGroupId: number | null | undefined,
): boolean {
  if (advisorGroupId == null) {
    return false;
  }

  return flagValue.advisorGroupIds.includes(advisorGroupId);
}

/**
 * Returns whether the account-connection-error feature is enabled for the
 * authenticated user, based on the Datadog object flag allowlist and the user's
 * advisor group. Independent of whether any specific account has an error, so
 * UI can decide between the legacy and new error indicators.
 *
 * The embeddable widget bundle always opts in, because it cannot evaluate the
 * flag at all: `@datadog/openfeature-browser` is aliased to a no-op stub (Salesforce
 * Lightning Web Security breaks RUM instrumentation) and `initializeDatadogFeatureFlags`
 * only runs from `main.tsx`, so OpenFeature falls back to its NoopProvider and every
 * flag resolves to the supplied default. The widget SSO payload also carries no advisor
 * group, so the allowlist could never match. Without this, widgets silently hide the
 * indicator on genuinely broken connections (EUD-784).
 */
export function useConnectionErrorEnabled(): boolean {
  const userInfo = useSelector((state: WARootState) => state.appView.UserInfo);
  const advisorGroupId = deriveAdvisorGroupId(userInfo);

  // suspend: false keeps grid cells and widgets from suspending while the flag
  // provider initializes; the feature stays hidden (empty allowlist) until then.
  const { value: connectionErrorFlag } =
    useObjectFeatureFlagDetails<ConnectionErrorFlagValue>(
      FEATURE_FLAGS.CONNECTION_ERROR,
      DEFAULT_CONNECTION_ERROR_FLAG,
      false,
    );

  if (isEmbeddableWidgetBundle) {
    return true;
  }

  return isConnectionErrorEnabledForAdvisorGroup(
    connectionErrorFlag,
    advisorGroupId,
  );
}

export interface AccountConnectionError {
  message: string;
  actionType: string;
  isActionable: boolean;
  flow: 'add' | 'edit' | 'refresh';
  externalId: number;
  financialInstitutionId: number;
  wealthId: number;
  institutionId: number;
  providerInstitutionId: number;
  /** Decides which reconnect flow applies: FastLink for Yodlee, the declarative form otherwise. */
  aggregatorId: number;
  /** Institution login URL for ConsumerSiteRequired "Go to Website". */
  institutionUrl: string;
}

/**
 * Resolves the connection-error state for an aggregated account by joining the
 * account's InstitutionId against the legacy wealth id in the investor Wealths
 * list. The error lives at the wealth level, so every account under a broken
 * wealth resolves the same error. Returns null when there is no error data.
 */
export function useAccountConnectionError(
  institutionId: number | undefined,
): AccountConnectionError | null {
  const isFeatureEnabled = useConnectionErrorEnabled();
  const { data } = useGetWealthsQuery({});

  if (!isFeatureEnabled || !institutionId || !data?.Records) {
    return null;
  }

  const wealth = data.Records.find((w) => w.WealthId === institutionId);

  if (!wealth || !wealth.HasError || !wealth.ErrorDetails) {
    return null;
  }

  const { ActionType, Message } = wealth.ErrorDetails;
  const aggregatorId = wealth.FinancialInstitution?.AggregatorId ?? 0;

  return {
    message: Message,
    actionType: ActionType,
    // The guard lives here, not in an early return: an unsupported aggregator
    // still has a real error worth showing — only "Fix It" is closed off.
    isActionable:
      isActionable(ActionType) && isSupportedAggregator(aggregatorId),
    flow: resolveFlow(ActionType, wealth.ExternalId),
    externalId: wealth.ExternalId,
    financialInstitutionId:
      wealth.FinancialInstitution?.FinancialInstitutionId ?? 0,
    wealthId: wealth.WealthId,
    institutionId,
    providerInstitutionId: wealth.FinancialInstitution?.InstitutionId ?? 0,
    aggregatorId,
    institutionUrl: wealth.FinancialInstitution?.InstitutionUrl ?? '',
  };
}
